import _objectSpread from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread";
import React from 'react';
import { Typography, useTheme, alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { observer } from 'mobx-react';
// core
import Base1DView from '@jbrowse/core/util/Base1DViewModel';
import { getSession, getTickDisplayStr } from '@jbrowse/core/util';
import { ContentBlock } from '@jbrowse/core/util/blockTypes';
// locals
import { HEADER_BAR_HEIGHT, HEADER_OVERVIEW_HEIGHT } from '..';
import { chooseGridPitch } from '../util';
import OverviewRubberband from './OverviewRubberband';
import Cytobands from './Cytobands';
import { getCytobands } from './util';
var wholeSeqSpacer = 2;
var useStyles = makeStyles()(function (theme) {
  return {
    scalebar: {
      height: HEADER_OVERVIEW_HEIGHT
    },
    scalebarBorder: {
      border: '1px solid'
    },
    scalebarContig: {
      backgroundColor: theme.palette.background.default,
      position: 'absolute',
      top: 0,
      height: HEADER_OVERVIEW_HEIGHT,
      overflow: 'hidden'
    },
    scalebarContigForward: {
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 15 9'%3E%3Cpath d='M-.1 0L6 4.5L-.1 9' fill='none' stroke='".concat(theme.palette.divider, "'/%3E%3C/svg%3E\")"),
      backgroundRepeat: 'repeat'
    },
    scalebarContigReverse: {
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 15 9'%3E%3Cpath d='M6 0L0 4.5L6 9' fill='none' stroke='".concat(theme.palette.divider, "'/%3E%3C/svg%3E\")"),
      backgroundRepeat: 'repeat'
    },
    scalebarRefName: {
      position: 'absolute',
      fontWeight: 'bold',
      pointerEvents: 'none',
      zIndex: 100
    },
    scalebarLabel: {
      height: HEADER_OVERVIEW_HEIGHT,
      position: 'absolute',
      display: 'flex',
      justifyContent: 'center',
      pointerEvents: 'none'
    },
    scalebarVisibleRegion: {
      position: 'absolute',
      height: HEADER_OVERVIEW_HEIGHT,
      pointerEvents: 'none',
      zIndex: 100,
      border: '1px solid'
    },
    overview: {
      height: HEADER_BAR_HEIGHT,
      position: 'relative'
    },
    overviewSvg: {
      width: '100%',
      position: 'absolute'
    }
  };
});
var Polygon = observer(function (_ref) {
  var model = _ref.model,
    overview = _ref.overview,
    _ref$useOffset = _ref.useOffset,
    useOffset = _ref$useOffset === void 0 ? true : _ref$useOffset;
  var theme = useTheme();
  var multiplier = Number(useOffset);
  var interRegionPaddingWidth = model.interRegionPaddingWidth,
    offsetPx = model.offsetPx,
    dynamicBlocks = model.dynamicBlocks,
    cytobandOffset = model.cytobandOffset;
  var contentBlocks = dynamicBlocks.contentBlocks,
    totalWidthPxWithoutBorders = dynamicBlocks.totalWidthPxWithoutBorders;
  var _theme$palette = theme.palette,
    tertiary = _theme$palette.tertiary,
    primary = _theme$palette.primary;
  var polygonColor = tertiary ? tertiary.light : primary.light;
  if (!contentBlocks.length) {
    return null;
  }
  var first = contentBlocks[0];
  var last = contentBlocks[contentBlocks.length - 1];
  var topLeft = (overview.bpToPx(_objectSpread({}, first, {
    coord: first.reversed ? first.end : first.start
  })) || 0) + cytobandOffset * multiplier;
  var topRight = (overview.bpToPx(_objectSpread({}, last, {
    coord: last.reversed ? last.start : last.end
  })) || 0) + cytobandOffset * multiplier;
  var startPx = Math.max(0, -offsetPx);
  var endPx = startPx + totalWidthPxWithoutBorders + contentBlocks.length * interRegionPaddingWidth / 2;
  var points = [[startPx, HEADER_BAR_HEIGHT], [endPx, HEADER_BAR_HEIGHT], [topRight, 0], [topLeft, 0]];
  return React.createElement("polygon", {
    points: points.toString(),
    fill: alpha(polygonColor, 0.3),
    stroke: alpha(polygonColor, 0.8)
  });
});
var OverviewBox = observer(function (_ref2) {
  var scale = _ref2.scale,
    model = _ref2.model,
    block = _ref2.block,
    overview = _ref2.overview;
  var _useStyles = useStyles(),
    classes = _useStyles.classes,
    cx = _useStyles.cx;
  var theme = useTheme();
  var cytobandOffset = model.cytobandOffset,
    showCytobands = model.showCytobands;
  var start = block.start,
    end = block.end,
    reversed = block.reversed,
    refName = block.refName,
    assemblyName = block.assemblyName;
  var _chooseGridPitch = chooseGridPitch(scale, 120, 15),
    majorPitch = _chooseGridPitch.majorPitch;
  var _getSession = getSession(model),
    assemblyManager = _getSession.assemblyManager;
  var assembly = assemblyManager.get(assemblyName);
  var refNameColor = assembly === null || assembly === void 0 ? void 0 : assembly.getRefNameColor(refName);
  var tickLabels = [];
  for (var i = 0; i < Math.floor((end - start) / majorPitch); i++) {
    var offsetLabel = (i + 1) * majorPitch;
    tickLabels.push(reversed ? end - offsetLabel : start + offsetLabel);
  }
  var canDisplayCytobands = showCytobands && getCytobands(assembly, block.refName).length;
  return React.createElement("div", null, React.createElement(Typography, {
    style: {
      left: block.offsetPx + 3,
      color: canDisplayCytobands ? theme.palette.primary.contrastText : refNameColor
    },
    className: classes.scalebarRefName
  }, refName), React.createElement("div", {
    className: cx(classes.scalebarContig, canDisplayCytobands ? undefined : reversed ? classes.scalebarContigReverse : classes.scalebarContigForward, !canDisplayCytobands ? classes.scalebarBorder : undefined),
    style: {
      left: block.offsetPx + cytobandOffset,
      width: block.widthPx,
      borderColor: refNameColor
    }
  }, !canDisplayCytobands ? tickLabels.map(function (tickLabel, labelIdx) {
    return React.createElement(Typography, {
      key: "".concat(JSON.stringify(block), "-").concat(tickLabel, "-").concat(labelIdx),
      className: classes.scalebarLabel,
      variant: "body2",
      style: {
        left: (labelIdx + 1) * majorPitch / scale,
        pointerEvents: 'none',
        color: refNameColor
      }
    }, getTickDisplayStr(tickLabel, overview.bpPerPx));
  }) : null, canDisplayCytobands ? React.createElement("svg", {
    style: {
      width: '100%'
    }
  }, React.createElement(Cytobands, {
    overview: overview,
    assembly: assembly,
    block: block
  })) : null));
});
var Scalebar = observer(function (_ref3) {
  var model = _ref3.model,
    scale = _ref3.scale,
    overview = _ref3.overview;
  var _useStyles2 = useStyles(),
    classes = _useStyles2.classes;
  var theme = useTheme();
  var dynamicBlocks = model.dynamicBlocks,
    showCytobands = model.showCytobands,
    cytobandOffset = model.cytobandOffset;
  var visibleRegions = dynamicBlocks.contentBlocks;
  var overviewVisibleRegions = overview.dynamicBlocks;
  var _theme$palette2 = theme.palette,
    tertiary = _theme$palette2.tertiary,
    primary = _theme$palette2.primary;
  var scalebarColor = tertiary ? tertiary.light : primary.light;
  if (!visibleRegions.length) {
    return null;
  }
  var first = visibleRegions[0];
  var firstOverviewPx = overview.bpToPx(_objectSpread({}, first, {
    coord: first.reversed ? first.end : first.start
  })) || 0;
  var last = visibleRegions[visibleRegions.length - 1];
  var lastOverviewPx = overview.bpToPx(_objectSpread({}, last, {
    coord: last.reversed ? last.start : last.end
  })) || 0;
  var color = showCytobands ? '#f00' : scalebarColor;
  var transparency = showCytobands ? 0.1 : 0.3;
  return React.createElement("div", {
    className: classes.scalebar
  }, React.createElement("div", {
    className: classes.scalebarVisibleRegion,
    style: {
      width: lastOverviewPx - firstOverviewPx,
      left: firstOverviewPx + cytobandOffset,
      background: alpha(color, transparency),
      borderColor: color
    }
  }), overviewVisibleRegions.map(function (block, idx) {
    return !(block instanceof ContentBlock) ? React.createElement("div", {
      key: "".concat(JSON.stringify(block), "-").concat(idx),
      className: classes.scalebarContig,
      style: {
        width: block.widthPx,
        left: block.offsetPx,
        backgroundColor: '#999',
        backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(255,255,255,.5) 1px, rgba(255,255,255,.5) 3px)'
      }
    }) : React.createElement(OverviewBox, {
      scale: scale,
      block: block,
      model: model,
      overview: overview,
      key: "".concat(JSON.stringify(block), "-").concat(idx)
    });
  }));
});
function OverviewScalebar(_ref4) {
  var model = _ref4.model,
    children = _ref4.children;
  var _useStyles3 = useStyles(),
    classes = _useStyles3.classes;
  var totalBp = model.totalBp,
    width = model.width,
    cytobandOffset = model.cytobandOffset,
    displayedRegions = model.displayedRegions;
  var overview = Base1DView.create({
    displayedRegions: JSON.parse(JSON.stringify(displayedRegions)),
    interRegionPaddingWidth: 0,
    minimumBlockWidth: model.minimumBlockWidth
  });
  var modWidth = width - cytobandOffset;
  overview.setVolatileWidth(modWidth);
  overview.showAllRegions();
  var scale = totalBp / (modWidth - (displayedRegions.length - 1) * wholeSeqSpacer);
  return React.createElement("div", null, React.createElement(OverviewRubberband, {
    model: model,
    overview: overview,
    ControlComponent: React.createElement(Scalebar, {
      model: model,
      overview: overview,
      scale: scale
    })
  }), React.createElement("div", {
    className: classes.overview
  }, React.createElement("svg", {
    height: HEADER_BAR_HEIGHT,
    className: classes.overviewSvg
  }, React.createElement(Polygon, {
    model: model,
    overview: overview
  })), children));
}
export default observer(OverviewScalebar);
export { Cytobands, Polygon };